import { FC, useState } from 'react';
import { useGetUserProjectsQuery, type Project } from '@teachur/http-client';
import { useSingleCourse } from '@new/features/courses/contexts/SingleCourseProvider';
import { LockedChip } from '@new/common/ui/Chip/LockedChip';
import { StepMessage } from '../StepMessage';
import { ProjectSelector } from './ProjectSelector';
import { ProjectStatusMessage } from './ProjectStatusMessage';
import { ProjectUpload } from './ProjectUpload';
import { ProjectSubmissions } from './ProjectSubmissions/ProjectSubmissions';
import { ProjectInfo } from './ProjectInfo';

export const ProjectContent: FC = () => {
  const { course, activeStep } = useSingleCourse();
  const projects = course?.projects || [];
  const isLocked = !course?.quizCompleted;

  const [selectedProject, setSelectedProject] = useState<Project | undefined>(projects[0]);

  const selectedProjectId = selectedProject?.id;

  const { data: userProjectsResponse } = useGetUserProjectsQuery(
    { courseId: course?.id },
    { enabled: activeStep === 'project' && !!course?.id }
  );

  const latestUserProject = userProjectsResponse?.data?.find(
    (up) => up.projectId === selectedProjectId
  );

  const userProjects =
    userProjectsResponse?.data?.filter((up) => up.projectId === selectedProjectId) ?? [];

  const hasSubmissions = userProjects.length > 0;

  if (activeStep !== 'project') return null;

  if (!projects.length || !selectedProjectId || !selectedProject) return null;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-6 rounded-2xl border-2 border-(--neutral-black-NB200) p-6 lg:p-8">
        {isLocked && (
          <StepMessage
            variant="warning"
            message="You need to complete the Course Exam with a 100% score first. Once you answer all exam questions correctly, the Course Project step will be unlocked."
          />
        )}
        <div className="flex flex-col gap-2.5">
          <h4 className="[font-size:var(--fs-h6)]! lg:[font-size:var(--fs-h4)]! text-(--neutral-black-NB1000)! font-bold m-0!">
            {selectedProject.name} {isLocked && <LockedChip />}
          </h4>
          <div className="h-[1.5px] bg-(--neutral-black-NB200)" />
        </div>

        <ProjectStatusMessage status={latestUserProject?.status} />
        <ProjectUpload selectedProjectId={selectedProjectId} status={latestUserProject?.status} />
        <ProjectSubmissions userProjects={userProjects} />
        <ProjectSelector
          projects={projects}
          selectedProjectId={selectedProjectId}
          onSelectProject={setSelectedProject}
        />

        <ProjectInfo project={selectedProject} hasSubmissions={hasSubmissions} />
      </div>
    </div>
  );
};
